'use client';

import { useCallback, useMemo, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { formatLength, type LengthUnit } from '@/lib/tools/units';
import {
  clampRoomSize,
  DEFAULT_ROOM_BOUNDS,
  pointerToRoomMeters,
  type RoomBounds,
} from '@/lib/tools/roomLayout';

export interface PlanMarker {
  id: string;
  /** Distance from left wall (m). */
  xM: number;
  /** Distance from front wall (m). */
  yM: number;
  label: string;
  color: string;
  draggable?: boolean;
}

export interface PlanDimLine {
  id: string;
  x1M: number;
  y1M: number;
  x2M: number;
  y2M: number;
  label?: string;
  offsetPx?: number;
}

export interface PlanRay {
  id: string;
  x1M: number;
  y1M: number;
  x2M: number;
  y2M: number;
  color?: string;
}

export interface PlanPoint {
  id: string;
  xM: number;
  yM: number;
  label?: string;
  color?: string;
}

interface Props {
  lengthM: number;
  widthM: number;
  unit: LengthUnit;
  markers?: PlanMarker[];
  dims?: PlanDimLine[];
  rays?: PlanRay[];
  points?: PlanPoint[];
  resizeWalls?: boolean;
  roomBounds?: RoomBounds;
  onRoomSizeChange?: (lengthM: number, widthM: number) => void;
  onMarkerMove?: (id: string, xM: number, yM: number) => void;
  maxWidthPx?: number;
  className?: string;
  testId?: string;
  /** Optional wall labels (default: Front only). */
  showWallLabels?: boolean;
}

const PAD = 36;

/**
 * SVG floor plan — front wall at top. X = width (left→right), Y = length (front→rear).
 * Wall-handle drags use unclamped pointer→meters so size can grow past the edge.
 */
export default function RoomPlan2D({
  lengthM,
  widthM,
  unit,
  markers = [],
  dims = [],
  rays = [],
  points = [],
  resizeWalls = false,
  roomBounds = DEFAULT_ROOM_BOUNDS,
  onRoomSizeChange,
  onMarkerMove,
  maxWidthPx = 360,
  className = '',
  testId = 'room-plan-2d',
  showWallLabels = true,
}: Props) {
  const svgRef = useRef<SVGSVGElement>(null);

  const { svgW, svgH, scale } = useMemo(() => {
    const scale = (maxWidthPx - PAD * 2) / Math.max(widthM, 0.1);
    return {
      svgW: widthM * scale + PAD * 2,
      svgH: lengthM * scale + PAD * 2,
      scale,
    };
  }, [lengthM, widthM, maxWidthPx]);

  const toSvg = useCallback(
    (xM: number, yM: number) => ({ x: PAD + xM * scale, y: PAD + yM * scale }),
    [scale],
  );

  const clientToMeters = useCallback(
    (clientX: number, clientY: number, forResize: boolean) => {
      const svg = svgRef.current;
      if (!svg) return { xM: 0, yM: 0 };
      return pointerToRoomMeters({
        clientX,
        clientY,
        svgRect: svg.getBoundingClientRect(),
        svgW,
        svgH,
        pad: PAD,
        scale,
        clampToRoom: forResize ? null : { lengthM, widthM },
      });
    },
    [svgW, svgH, scale, lengthM, widthM],
  );

  const dragMarker = (id: string) => (e: ReactPointerEvent) => {
    if (!onMarkerMove) return;
    e.preventDefault();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const move = (ev: PointerEvent) => {
      const p = clientToMeters(ev.clientX, ev.clientY, false);
      onMarkerMove(id, p.xM, p.yM);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const dragWall = (edge: 'right' | 'bottom') => (e: ReactPointerEvent) => {
    if (!onRoomSizeChange) return;
    e.preventDefault();
    e.stopPropagation();
    (e.target as Element).setPointerCapture?.(e.pointerId);
    const move = (ev: PointerEvent) => {
      // Unclamped — growing past the current edge must stay possible at min size.
      const p = clientToMeters(ev.clientX, ev.clientY, true);
      if (edge === 'right') {
        const next = clampRoomSize(lengthM, p.xM, roomBounds);
        onRoomSizeChange(lengthM, next.widthM);
      } else {
        const next = clampRoomSize(p.yM, widthM, roomBounds);
        onRoomSizeChange(next.lengthM, widthM);
      }
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
  };

  const room = { x: PAD, y: PAD, w: widthM * scale, h: lengthM * scale };

  return (
    <div className={`mx-auto ${className}`} data-testid={testId}>
      <svg
        data-seo="diagram"
        ref={svgRef}
        width={svgW}
        height={svgH}
        viewBox={`0 0 ${svgW} ${svgH}`}
        className="max-w-full rounded-lg border border-[var(--app-border)] bg-[var(--app-bg)] select-none touch-none"
        role="img"
        aria-label="Room floor plan"
      >
        {showWallLabels && (
          <>
            <text x={PAD + room.w / 2} y={14} textAnchor="middle" fill="var(--rt-ink-dim)" className="text-overline">
              Front
            </text>
            <text
              x={PAD + room.w / 2}
              y={PAD + room.h + 22}
              textAnchor="middle"
              fill="var(--rt-ink-dim)"
              className="text-overline"
            >
              Rear
            </text>
            <text
              x={12}
              y={PAD + room.h / 2}
              textAnchor="middle"
              fill="var(--rt-ink-dim)"
              className="text-overline"
              transform={`rotate(-90 12 ${PAD + room.h / 2})`}
            >
              Left
            </text>
            <text
              x={PAD + room.w + 24}
              y={PAD + room.h / 2}
              textAnchor="middle"
              fill="var(--rt-ink-dim)"
              className="text-overline"
              transform={`rotate(90 ${PAD + room.w + 24} ${PAD + room.h / 2})`}
            >
              Right
            </text>
          </>
        )}
        <rect
          x={room.x}
          y={room.y}
          width={room.w}
          height={room.h}
          fill="color-mix(in oklch, var(--rt-accent) 4%, transparent)"
          stroke="var(--rt-ink-dim)"
          strokeWidth={1.5}
        />

        {rays.map((r) => {
          const a = toSvg(r.x1M, r.y1M);
          const b = toSvg(r.x2M, r.y2M);
          return (
            <line
              key={r.id}
              x1={a.x}
              y1={a.y}
              x2={b.x}
              y2={b.y}
              stroke={r.color ?? 'color-mix(in srgb, var(--rt-accent) 55%, transparent)'}
              strokeWidth={1.25}
            />
          );
        })}

        {dims.map((d) => {
          const a = toSvg(d.x1M, d.y1M);
          const b = toSvg(d.x2M, d.y2M);
          const dx = b.x - a.x;
          const dy = b.y - a.y;
          const len = Math.hypot(dx, dy) || 1;
          const ox = (-dy / len) * (d.offsetPx ?? 14);
          const oy = (dx / len) * (d.offsetPx ?? 14);
          const ax = a.x + ox;
          const ay = a.y + oy;
          const bx = b.x + ox;
          const by = b.y + oy;
          const midX = (ax + bx) / 2;
          const midY = (ay + by) / 2;
          const distM = Math.hypot(d.x2M - d.x1M, d.y2M - d.y1M);
          const label = d.label ?? formatLength(distM, unit);
          return (
            <g key={d.id} data-testid={`dim-${d.id}`}>
              <line x1={ax} y1={ay} x2={bx} y2={by} stroke="var(--rt-accent)" strokeWidth={1} />
              <line x1={a.x} y1={a.y} x2={ax} y2={ay} stroke="var(--rt-accent)" strokeWidth={0.75} opacity={0.5} />
              <line x1={b.x} y1={b.y} x2={bx} y2={by} stroke="var(--rt-accent)" strokeWidth={0.75} opacity={0.5} />
              <rect
                x={midX - label.length * 3.2}
                y={midY - 8}
                width={label.length * 6.4}
                height={14}
                rx={2}
                fill="var(--app-bg)"
                opacity={0.9}
              />
              <text
                x={midX}
                y={midY + 3}
                textAnchor="middle"
                fill="var(--rt-accent)"
                className="text-overline"
                fontFamily="ui-monospace, monospace"
              >
                {label}
              </text>
            </g>
          );
        })}

        {points.map((p) => {
          const { x, y } = toSvg(p.xM, p.yM);
          return (
            <g key={p.id}>
              <circle cx={x} cy={y} r={5} fill={p.color ?? 'var(--rt-accent)'} stroke="var(--rt-button-label)" strokeWidth={1} />
              {p.label && (
                <text x={x + 8} y={y + 3} fill="var(--rt-accent-ink)" className="text-overline">
                  {p.label}
                </text>
              )}
            </g>
          );
        })}

        {markers.map((m) => {
          const { x, y } = toSvg(m.xM, m.yM);
          const drag = m.draggable !== false && !!onMarkerMove;
          return (
            <g
              key={m.id}
              transform={`translate(${x},${y})`}
              style={{ cursor: drag ? 'grab' : 'default' }}
              onPointerDown={drag ? dragMarker(m.id) : undefined}
              data-testid={`marker-${m.id}`}
            >
              <circle r={9} fill={m.color} stroke="var(--rt-button-label)" strokeWidth={1.25} />
              <text
                textAnchor="middle"
                y={3.5}
                fill="var(--rt-button-label)"
                className="text-overline"
                fontWeight={700}
                style={{ pointerEvents: 'none' }}
              >
                {m.label}
              </text>
            </g>
          );
        })}

        {resizeWalls && onRoomSizeChange && (
          <>
            <rect
              x={room.x + room.w - 5}
              y={room.y + room.h / 2 - 16}
              width={10}
              height={32}
              rx={3}
              fill="var(--rt-accent)"
              opacity={0.85}
              style={{ cursor: 'ew-resize' }}
              onPointerDown={dragWall('right')}
              data-testid="wall-handle-width"
            />
            <rect
              x={room.x + room.w / 2 - 16}
              y={room.y + room.h - 5}
              width={32}
              height={10}
              rx={3}
              fill="var(--rt-accent)"
              opacity={0.85}
              style={{ cursor: 'ns-resize' }}
              onPointerDown={dragWall('bottom')}
              data-testid="wall-handle-length"
            />
          </>
        )}
      </svg>
    </div>
  );
}
